import { Routes } from '@angular/router';
import { EmailNotVerifiedComponent } from './components/auth/email-not-verified/email-not-verified.component';
import { ImpersonateUserComponent } from './components/auth/impersonate-user/impersonate-user.component';
import { RegisterUserComponent } from './components/auth/register-user/register-user.component';
import { ResetPasswordComponent } from './components/auth/reset-password/reset-password.component';
import { SignInComponent } from './components/auth/sign-in/sign-in.component';
import { DashboardComponent } from './components/dashboard/dashboard.component';
import { authGuard } from './guards/auth/auth.guard';
import { managerGuard } from './guards/manager/manager.guard';
import { moduleGuard } from './guards/module/module.guard';
import { ownerGuard } from './guards/owner/owner.guard';
import { verifyDealerUserGuard } from './guards/verify-dealer-user/verify-dealer-user.guard';
import { verifyEmailGuard } from './guards/verify-email/verify-email.guard';
import { SEOResolver } from './resolvers/seo.resolver';
import { unsavedChangesGuard } from './components/setup/receipt/unsaved-changes.guard';

export const routes: Routes = [
  /******************************************************************************************
   * Eagerly-Loaded Routes
   ******************************************************************************************/
  {
    path: 'dashboard',
    component: DashboardComponent,
    canActivate: [authGuard],
    data: {
      seoParams: {
        metaTitle: 'Dashboard',
      },
    },
    resolve: {
      SEOResolver,
    },
  },
  {
    path: 'email-not-verified',
    component: EmailNotVerifiedComponent,
    canActivate: [verifyEmailGuard],
    data: {
      seoParams: {
        metaTitle: 'Email Verification Warning',
      },
    },
    resolve: {
      SEOResolver,
    },
  },
  {
    path: 'forgot-password',
    component: ResetPasswordComponent,
    // auth - intentionally unprotected
    data: {
      seoParams: {
        metaTitle: 'Forgot Password',
      },
    },
    resolve: {
      SEOResolver,
    },
  },
  {
    path: 'impersonate',
    component: ImpersonateUserComponent,
    // auth - intentionally unprotected
    data: {
      seoParams: {
        metaTitle: 'Impersonate User',
      },
    },
    resolve: {
      SEOResolver,
    },
  },
  {
    path: 'register/:firestoreId/:email',
    component: RegisterUserComponent,
    // auth - intentionally unprotected
    data: {
      seoParams: {
        metaTitle: 'User Registration',
      },
    },
    resolve: {
      SEOResolver,
    },
  },
  {
    path: 'sign-in',
    component: SignInComponent,
    // auth - intentionally unprotected
    data: {
      seoParams: {
        metaTitle: 'Sign-In',
      },
    },
    resolve: {
      SEOResolver,
    },
  },

  /******************************************************************************************
   * Lazy-Loaded Routes
   ******************************************************************************************/
  {
    path: 'auth-email-action',
    // auth - intentionally unprotected
    loadComponent: () =>
      import('@jfw/angular/ecommerce/common-components/auth-email-actions').then(
        (c) => c.AuthEmailActionContainerComponent,
      ),
    data: {
      seoParams: {
        metaTitle: 'Email Verification',
      },
    },
    resolve: {
      SEOResolver,
    },
  },
  {
    path: 'become-a-referral-partner',
    // content - intentionally unprotected
    loadComponent: () =>
      import('./components/content/become-a-referral-partner/become-a-referral-partner.component').then(
        (c) => c.BecomeAReferralPartnerComponent,
      ),
    data: {
      seoParams: {
        metaTitle: 'Become a Referral Partner',
      },
    },
    resolve: {
      SEOResolver,
    },
  },
  {
    path: 'become-a-referral-partner-thank-you',
    // content - intentionally unprotected
    loadComponent: () =>
      import('./components/content/become-a-referral-partner/thank-you/thank-you.component').then(
        (c) => c.BecomeAReferralPartnerThankYouComponent,
      ),
    data: {
      seoParams: {
        metaTitle: 'Become a Referral Partner Thank You',
        metaDescription:
          'Thank you for your interest in becoming a JFW referral partner. A member of our team will be in touch with you soon.',
      },
    },
    resolve: { SEOResolver },
  },
  {
    path: 'customize-look',
    canActivate: [authGuard, moduleGuard],
    loadChildren: () =>
      import('@jfw/angular/ecommerce/feature-modules/customize-look').then(
        (m) => m.CustomizeLookModule,
      ),
    data: {
      newSiteOnly: false,
      seoParams: {
        metaTitle: 'Customize Your Look',
      },
    },
    resolve: {
      SEOResolver,
    },
  },
  {
    path: 'ensembles/:category',
    canActivate: [authGuard, moduleGuard],
    data: {
      newSiteOnly: false,
      seoParams: {
        metaTitle: 'Suits and Tuxedos',
      },
    },
    resolve: {
      SEOResolver,
    },
    loadComponent: () =>
      import('./components/listing-pages/ensemble-listing-page/ensemble-listing-page.component').then(
        (c) => c.EnsembleListingPageComponent,
      ),
  },
  {
    path: 'event',
    canActivate: [authGuard, verifyDealerUserGuard, moduleGuard],
    data: {
      newSiteOnly: false,
      seoParams: {
        metaTitle: 'Referral Event Info',
      },
    },
    resolve: {
      SEOResolver,
    },
    loadChildren: () =>
      import('@jfw/angular/ecommerce/feature-modules/event-manager').then(
        (m) => m.EventManagerModule,
      ),
  },
  {
    path: 'event-details/:eventId',
    canActivate: [authGuard, moduleGuard],
    data: {
      newSiteOnly: false,
      seoParams: {
        metaTitle: 'Referral Event Details',
      },
    },
    resolve: {
      SEOResolver,
    },
    loadComponent: () =>
      import('./components/event/details/event-details.component').then(
        (c) => c.EventDetailsComponent,
      ),
  },
  {
    path: 'event-planner',
    canActivate: [authGuard, moduleGuard],
    data: {
      newSiteOnly: true,
      seoParams: {
        metaTitle: 'Wholesale Event Planner',
      },
    },
    resolve: {
      SEOResolver,
    },
    loadComponent: () =>
      import('./components/event/planner/planner.component').then(
        (c) => c.EventPlannerComponent,
      ),
  },
  {
    path: 'event-search',
    canActivate: [authGuard, moduleGuard],
    data: {
      newSiteOnly: false,
      seoParams: {
        metaTitle: 'Event Search',
      },
    },
    resolve: {
      SEOResolver,
    },
    loadComponent: () =>
      import('./components/event/search-results/event-search-results.component').then(
        (c) => c.EventSearchResultsComponent,
      ),
  },
  {
    path: 'legacy-event-search',
    redirectTo: 'search',
  },
  {
    path: 'referral-program-information',
    // content - intentionally unprotected
    data: {
      seoParams: {
        metaTitle: 'Referral Program Information',
      },
    },
    resolve: {
      SEOResolver,
    },
    loadComponent: () =>
      import('./components/content/referral-program-information/referral-program-information.component').then(
        (c) => c.ReferralProgramInformationComponent,
      ),
  },
  {
    path: 'register-new-event',
    canActivate: [authGuard, moduleGuard],
    data: {
      newSiteOnly: false,
      seoParams: {
        metaTitle: 'New Referral Event',
      },
    },
    resolve: {
      SEOResolver,
    },
    loadComponent: () =>
      import('./components/event/register-new/register-new-event.component').then(
        (m) => m.RegisterNewEventComponent,
      ),
  },
  {
    path: 'referral-dashboard',
    canActivate: [authGuard, moduleGuard],
    data: {
      newSiteOnly: false,
      seoParams: {
        metaTitle: 'Referral Dashboard',
      },
    },
    resolve: {
      SEOResolver,
    },
    loadComponent: () =>
      import('./components/referral-dashboard/referral-dashboard.component').then(
        (m) => m.ReferralDashboardComponent,
      ),
  },
  {
    path: 'reports',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./components/reports/reports.component').then(
        (c) => c.ReportsComponent,
      ),
    children: [
      {
        path: 'account-balance',
        canActivate: [moduleGuard],
        data: {
          newSiteOnly: true,
          seoParams: {
            metaTitle: 'Wholesale Account Balance',
          },
        },
        resolve: {
          SEOResolver,
        },
        loadComponent: () =>
          import('./components/reports/account-balance/account-balance.component').then(
            (c) => c.AccountBalanceComponent,
          ),
      },
      {
        path: 'commission-statements',
        canActivate: [ownerGuard, moduleGuard],
        data: {
          newSiteOnly: false,
          seoParams: {
            metaTitle: 'Referral Commission Statements',
          },
        },
        resolve: {
          SEOResolver,
        },
        loadComponent: () =>
          import('./components/reports/commission/statements/statements.component').then(
            (c) => c.CommissionsStatementsComponent,
          ),
      },
      {
        path: 'commission-statements/:accountGroupingId',
        canActivate: [ownerGuard, moduleGuard],
        data: {
          newSiteOnly: false,
          seoParams: {
            metaTitle: 'Referral Commission Details',
          },
        },
        resolve: {
          SEOResolver,
        },
        loadComponent: () =>
          import('./components/reports/commission/statement-details/statement-details.component').then(
            (c) => c.CommissionStatementDetailsComponent,
          ),
      },
      {
        path: 'billing-history',
        canActivate: [moduleGuard],
        data: {
          newSiteOnly: true,
          seoParams: {
            metaTitle: 'Wholesale Billing History',
          },
        },
        resolve: {
          SEOResolver,
        },
        loadComponent: () =>
          import(
            './components/reports/billing-history/billing-history.component'
          ).then((c) => c.BillingHistoryComponent),
      },
      {
        path: 'low-inventory',
        canActivate: [moduleGuard],
        data: {
          newSiteOnly: true,
          seoParams: {
            metaTitle: 'Low Inventory',
          },
        },
        resolve: {
          SEOResolver,
        },
        loadComponent: () =>
          import('./components/reports/low-inventory/low-inventory.component').then(
            (c) => c.LowInventoryComponent,
          ),
      },
      {
        path: 'order-history',
        canActivate: [moduleGuard],
        data: {
          newSiteOnly: true,
          seoParams: {
            metaTitle: 'Wholesale Order History',
          },
        },
        resolve: {
          SEOResolver,
        },
        loadComponent: () =>
          import('./components/reports/order-history/order-history.component').then(
            (c) => c.OrderHistoryComponent,
          ),
      },
      {
        path: 'pack-slips',
        canActivate: [moduleGuard],
        data: {
          newSiteOnly: true,
          seoParams: {
            metaTitle: 'Wholesale Packing Slips',
          },
        },
        resolve: {
          SEOResolver,
        },
        loadComponent: () =>
          import('./components/reports/pack-slips/pack-slips.component').then(
            (c) => c.PackSlipsComponent,
          ),
      },
      {
        path: 'pricing-inquiry',
        canActivate: [moduleGuard],
        data: {
          newSiteOnly: true,
          seoParams: {
            metaTitle: 'Wholesale Pricing Inquiry',
          },
        },
        resolve: {
          SEOResolver,
        },
        loadComponent: () =>
          import('./components/reports/pricing-inquiry/pricing-inquiry.component').then(
            (c) => c.PricingInquiryComponent,
          ),
      },
      {
        path: 'shipping-inquiry',
        canActivate: [moduleGuard],
        data: {
          newSiteOnly: true,
          seoParams: {
            metaTitle: 'Shipping Inquiry',
          },
        },
        resolve: {
          SEOResolver,
        },
        loadComponent: () =>
          import('./components/reports/shipping-inquiry/shipping-inquiry.component').then(
            (c) => c.ShippingInquiryComponent,
          ),
      },
    ],
  },
  {
    path: 'setup',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./components/setup/setup.component').then(
        (c) => c.SetupComponent,
      ),
    children: [
      {
        path: 'account-info',
        loadComponent: () =>
          import('./components/setup/account-info/account-info.component').then(
            (c) => c.AccountInfoComponent,
          ),
        data: {
          newSiteOnly: false,
          seoParams: {
            metaTitle: 'Account Info',
          },
        },
        resolve: {
          SEOResolver,
        },
      },
      {
        path: 'retailer-webpage',
        canActivate: [moduleGuard],
        data: {
          newSiteOnly: true,
          seoParams: {
            metaTitle: 'Retailer Webpage',
          },
        },
        resolve: {
          SEOResolver,
        },
        loadComponent: () =>
          import('./components/setup/retailer-webpage/retailer-webpage.component').then(
            (c) => c.RetailerWebpageComponent,
          ),
      },
      {
        path: 'receipt',
        canActivate: [moduleGuard],
        data: {
          newSiteOnly: true,
          seoParams: {
            metaTitle: 'Receipt Management',
          },
        },
        resolve: {
          SEOResolver,
        },
        loadComponent: () =>
          import('./components/setup/receipt/receipt.component').then(
            (c) => c.ReceiptComponent,
          ),
        children: [
          { path: '', redirectTo: 'pricing', pathMatch: 'full' },
          {
            path: 'pricing',
            canDeactivate: [unsavedChangesGuard],
            loadComponent: () =>
              import(
                './components/setup/receipt/pricing/receipt-pricing-page.component'
              ).then((c) => c.ReceiptPricingPageComponent),
          },
          {
            path: 'taxes',
            canDeactivate: [unsavedChangesGuard],
            loadComponent: () =>
              import(
                './components/setup/receipt/taxes/receipt-taxes.component'
              ).then((c) => c.ReceiptTaxesComponent),
          },
          {
            path: 'store-address',
            canDeactivate: [unsavedChangesGuard],
            loadComponent: () =>
              import(
                './components/setup/receipt/store-address/receipt-store-address.component'
              ).then((c) => c.ReceiptStoreAddressComponent),
          },
          {
            path: 'rental-agreement',
            canDeactivate: [unsavedChangesGuard],
            loadComponent: () =>
              import(
                './components/setup/receipt/rental-agreement/receipt-rental-agreement.component'
              ).then((c) => c.ReceiptRentalAgreementComponent),
          },
          {
            path: 'promotions',
            canDeactivate: [unsavedChangesGuard],
            loadComponent: () =>
              import(
                './components/setup/receipt/promotions/receipt-promotions.component'
              ).then((c) => c.ReceiptPromotionsComponent),
          },
          {
            path: 'store-styles',
            canDeactivate: [unsavedChangesGuard],
            loadComponent: () =>
              import(
                './components/setup/receipt/store-styles/receipt-store-styles.component'
              ).then((c) => c.ReceiptStoreStylesComponent),
          },
        ],
      },
      {
        path: 'users/create',
        canActivate: [managerGuard, moduleGuard],
        data: {
          newSiteOnly: false,
          seoParams: {
            metaTitle: 'Create Users',
          },
        },
        resolve: {
          SEOResolver,
        },
        loadComponent: () =>
          import('./components/setup/users/create/create.component').then(
            (c) => c.CreateUserComponent,
          ),
      },
      {
        path: 'users/manage',
        canActivate: [managerGuard, moduleGuard],
        data: {
          newSiteOnly: false,
          seoParams: {
            metaTitle: 'Manage Users',
          },
        },
        resolve: {
          SEOResolver,
        },
        loadComponent: () =>
          import('./components/setup/users/manage/manage.component').then(
            (c) => c.ManageUsersComponent,
          ),
      },
    ],
  },
  {
    path: 'support',
    canActivate: [authGuard, moduleGuard],
    data: {
      newSiteOnly: false,
      seoParams: {
        metaTitle: 'Support',
      },
    },
    resolve: {
      SEOResolver,
    },
    loadComponent: () =>
      import('./components/support/support.component').then(
        (c) => c.SupportComponent,
      ),
  },
  {
    path: 'free-swatches',
    canActivate: [authGuard, moduleGuard],
    data: {
      newSiteOnly: false,
      seoParams: {
        metaTitle: 'Free Swatches',
      },
    },
    resolve: {
      SEOResolver,
    },
    loadComponent: () =>
      import(
        './components/content/free-swatches/free-swatches.component'
      ).then((c) => c.FreeSwatchesComponent),
  },
  {
    path: 'resource-center',
    canActivate: [authGuard, moduleGuard],
    data: {
      newSiteOnly: true,
      seoParams: {
        metaTitle: 'Resource Center',
      },
    },
    resolve: {
      SEOResolver,
    },
    loadComponent: () =>
      import(
        './components/resource-center/resource-center.component'
      ).then((c) => c.ResourceCenterComponent),
  },
  {
    path: 'training-videos',
    canActivate: [authGuard, moduleGuard],
    data: {
      newSiteOnly: true,
      seoParams: {
        metaTitle: 'Training Videos',
      },
    },
    resolve: {
      SEOResolver,
    },
    loadComponent: () =>
      import(
        './components/training-videos/training-videos.component'
      ).then((c) => c.TrainingVideosComponent),
  },
];

routes.push(
  {
    path: 'order-entry',
    canActivate: [authGuard, moduleGuard],
    data: {
      newSiteOnly: true,
      seoParams: {
        metaTitle: 'Order Entry',
      },
    },
    resolve: {
      SEOResolver,
    },
    loadComponent: () =>
      import('./components/order/entry/entry.component').then(
        (c) => c.OrderEntryComponent,
      ),
  },
  {
    path: 'receipt-viewer',
    canActivate: [authGuard, moduleGuard],
    data: {
      newSiteOnly: true,
      seoParams: {
        metaTitle: 'Receipt',
      },
    },
    resolve: {
      SEOResolver,
    },
    loadComponent: () =>
      import('./components/receipt/viewer/receipt-viewer.component').then(
        (c) => c.ReceiptViewerComponent,
      ),
  },
  {
    path: 'search',
    canActivate: [authGuard, moduleGuard],
    data: {
      newSiteOnly: true,
      seoParams: {
        metaTitle: 'Search',
      },
    },
    resolve: {
      SEOResolver,
    },
    loadComponent: () =>
      import('./components/search/search.component').then(
        (c) => c.SearchComponent,
      ),
  },
  {
    path: 'order-search',
    redirectTo: 'search',
  },
);

routes.push({ path: '**', redirectTo: '/dashboard' });
